import appConstants from "../../../../../../appConstants";
import { COUNTRIES, COUNTRY_LIST } from "../../constants";
import { v4 as uuidv4 } from "uuid";

const getDefaultShipmentData = ({
  getProNumber,
  shipment,
  proNumberLength,
  defaultCountry,
  stopData = null
}) => {
  const isProPreAssigned =
    shipment?.proId === "0" ? false : Boolean(shipment?.proId);
  return {
    proNumber: getProNumber({
      proNumber: shipment?.proId ?? "",
      isProPreAssigned: isProPreAssigned,
      proNumberLength: proNumberLength
    }),
    pickupId: shipment?.pickupId || stopData?.pickupId || "",
    isProPreAssigned,
    accessorials: [],
    priority: shipment?.priority ?? "",
    isFood: shipment?.isFood || false,
    isExcessiveLength: shipment?.isExcessiveLength ? appConstants.EXCESSIVE_SHIPMENT.YES : appConstants.EXCESSIVE_SHIPMENT.NO,
    isFreezable: shipment?.isFreezable || false,
    isAmmunition: shipment?.isAmmunition || false,
    isHazmat: shipment?.isHazmat || false,
    excessiveProLength: "",
    consigneeName: shipment?.consigneeName || "",
    consigneeCity: shipment?.consigneeCity || "",
    consigneeCountry: shipment?.consigneeCountry || defaultCountry || COUNTRIES.US,
    consigneeZip: shipment?.consigneeZip || "",
    pieces: shipment?.pieces || "",
    weight: shipment?.weight || "",
    packagingItems: [
      { hu: shipment?.hu || "", packageCode: shipment?.packageCode || "" },
    ],
    hazmatInfo: shipment?.hazmatInfo || [],
    comment: shipment?.comment || "",
    consigneeAddress1: shipment?.consigneeAddress1 || "",
    consigneeAddress2: shipment?.consigneeAddress2 || "",
    consigneeCode: shipment?.consigneeCode || "",
    consigneeState: shipment?.consigneeState || "",
    ediProNumber: shipment?.ediProNumber || "",
    limitedAccessCode: shipment?.limitedAccessCode || "",
    receivedCanadianDocumentation:
      shipment?.receivedCanadianDocumentation || false
  };
};

const getDefaultPickupStopInfo = ({getProNumber, stopData, proNumberLength, defaultCountry}) => {
  const isStopCompleted = stopData.isStopCompleted;
  return stopData.shipments
    .map((shipment) => {
      const defaultShipmentData = getDefaultShipmentData({
        getProNumber,
        shipment,
        proNumberLength,
        defaultCountry,
        stopData
      });
      return {
        shipmentId: shipment.shipmentId ?? uuidv4(),
        editInfo: defaultShipmentData,
        errorInfo: {
          packagingItemsErrorInfo: [{ hu: null, packageCode: null }],
        },
        openEditMode: false,
        initialDataExists: true,
        isUpdatedAtLeastOnce: false,
        ...(isStopCompleted && { isCompletedPro: true }),
        pickupStatus: isStopCompleted ? appConstants.PICKUP_STATUS.COMPLETED: appConstants.PICKUP_STATUS.WAITING,
      };
    })
    .filter((data) => data);
};

const getPriorityShipmentList = (priorityShipmentList) => {
  return priorityShipmentList.map((data) => ({
    value: data.value,
    labelText: data.label,
  }));
};

const getExcessiveShipmentList = () => {
  return appConstants.EXCESSIVE_SHIPMENT_OPTIONS.map((data) => ({
    value: data.statusCode,
    labelText: data.status,
  }));
};

const getCountryList = () => {
  return COUNTRY_LIST.map((data) => ({
    value: data.code,
    label: data.name,
  }));
};

const getPickupIdToNumberMap = (shipments = []) => {
  // Build a map of unique pickupId to pickupNumber, skipping null/undefined pickupIds
  return shipments.reduce((acc, { pickupId, pickupNumber }) => {
    if (pickupId != null && !(pickupId in acc)) {
      acc[pickupId] = pickupNumber;
    }
    return acc;
  }, {});
};

const getPickupNumberOptions = (shipments = []) => {
  const pickupIdToNumber = getPickupIdToNumberMap(shipments);
  return Object.entries(pickupIdToNumber).map(([pickupId, pickupNumber]) => ({
    value: pickupId,
    label: pickupNumber,
  }));
};

// Comparator for Shipment objects (no original index needed)
const sortShipmentsByPickupNumberComparator = (firstShipment, secondShipment) => {
  const firstIsNew = firstShipment?.openEditMode;
  const secondIsNew = secondShipment?.openEditMode;
  if (firstIsNew && !secondIsNew) return 1; // keep newly added shipments at the end
  if (!firstIsNew && secondIsNew) return -1; // existing shipments first
  const firstPickup = firstShipment?.editInfo?.pickupId || '';
  const secondPickup = secondShipment?.editInfo?.pickupId || '';
  return firstPickup.localeCompare(secondPickup);
};

const getSortedShipmentsByPickupNumber = (editBillsDataArray) => {
  const sortedShipments = [...editBillsDataArray].sort(
    sortShipmentsByPickupNumberComparator
  );
  const pickupCountersByNumber = {};
  return sortedShipments.map((shipment) => {
    const pickupKey = shipment?.editInfo?.pickupId || '';
    pickupCountersByNumber[pickupKey] = (pickupCountersByNumber[pickupKey] || 0) + 1;
    return { shipment, pickupIndex: pickupCountersByNumber[pickupKey] };
  });
};

const getNoFreightPickupIds = (editBillsDataArray) => {
  const pickupStatusMap = {};

  editBillsDataArray.forEach(({ editInfo, pickupStatus }) => {
    const pickupId = editInfo?.pickupId;
    if (pickupId) {
      if (!pickupStatusMap[pickupId]) pickupStatusMap[pickupId] = [];
      pickupStatusMap[pickupId].push(pickupStatus);
    }
  });

  return Object.entries(pickupStatusMap)
    .filter(([, statuses]) =>
      statuses.every((status) => status !== appConstants.PICKUP_STATUS.COMPLETED)
    )
    .map(([pickupId]) => pickupId);
};

export {
  getDefaultPickupStopInfo,
  getDefaultShipmentData,
  getPriorityShipmentList,
  getCountryList,
  getExcessiveShipmentList,
  getPickupIdToNumberMap,
  getPickupNumberOptions,
  getSortedShipmentsByPickupNumber,
  getNoFreightPickupIds
};
